/**
 *
 * ExamCard
 *
 */
import { Box, Card, Stack, Typography, CircularProgress } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SubjectIcon from "app/components/SubjectIcon/SubjectIcon";
import ExamStatus from "app/components/ExamStatus/ExamStatus";
import { TypeExam, TypeExamStatus } from "app/constants/types/exam.types";
import { selectIsExamEnrolled, selectExamStore } from "store/ExamStore/slice/selector";
import { mapExamStatus } from "app/components/ExamStatus/utils/mapExamStatus";
import { media } from "styles/media";
import dayjs from "dayjs";
import { DATE_FORMAT, TIME_FORMAT } from "app/constants/content";

interface Props {
  exam: TypeExam & { originalStatus?: string };
  isLoading?: boolean;
}

export function ExamCard({ exam, isLoading: externalLoading }: Props) {
  const {
    examId,
    examDuration,
    updatedAt,
    status,
    createdAt,
    examTime,
    description,
    title,
    subject,
    level,
    rawData,
    originalStatus = status,
  } = exam;

  const navigate = useNavigate();
  
  // Get error states from the store
  const { error, enrolledExamsError } = useSelector(selectExamStore);
  
  // Use external loading state if provided, otherwise false
  const isLoading = externalLoading || false;
  
  // Determine if there's an error
  const hasError = error || enrolledExamsError;
  
  // Get enrollment status from selector
  const isEnrolled = useSelector(selectIsExamEnrolled(examId));
  
  // Check if the user has a submission for this exam
  const hasSubmission = status === "submitted";
  
  // Calculate mapped status based on enrollment, handle possible undefined rawData
  const mappedStatus = mapExamStatus(
    rawData?.status || status, // Fallback to status if rawData.status is undefined
    isEnrolled,
    examTime,
    examDuration,
    hasSubmission,
  );

  const handleViewExam = (examId: string) => {
    // Navigation logic based on mapped status
    if (mappedStatus === "standby" || mappedStatus === "close") {
      return null; // Enrolled users can't navigate to locked exams
    }
    
    // Navigate to checkout page for non-enrolled exams (open status)
    if (mappedStatus === "open") {
      navigate(`/exam/checkout/${examId}`);
    } else {
      // For all other cases, navigate to student exam page
      navigate(`/student/${examId}`);
    }
  };

  const isActive = !["close"].includes(mappedStatus as string) && !["standby"].includes(mappedStatus as string) && !["standby-locked"].includes(mappedStatus as string)

  return (
    <Card
      variant="outlined"
      sx={{
        p: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: "120px",
        minWidth: "280px",
        width: "100%",
        transition: "all 0.4s ease-in-out",
        borderRadius: "16px",
        backgroundColor: "background.surface",
        borderWidth: "1px",
        position: "relative", // Add this for absolute positioning of loading indicator
        opacity: isLoading ? 0.7 : 1, // Dim the card during loading
        "&:hover": {
          backgroundColor: isActive && !isLoading
            ? "neutral.100"
            : undefined,
        },
        [media.small()]: {
          minHeight: "130px",
        },
      }}
    >
      {/* Add loading indicator overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: '24px',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        >
          <CircularProgress size="sm" />
        </Box>
      )}
      <Box
        component="button"
        onClick={() => handleViewExam(examId)}
        sx={{
          py: 2.5,
          px: { xs: 2, sm: 3, md: 4 }, // Responsive padding
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          borderRadius: "inherit",
          backgroundColor: "transparent",
          cursor: isActive
            ? "pointer"
            : undefined,
          borderColor: "transparent",
          overflow: "hidden", // Prevent content overflow
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          sx={{
            display: "grid",
            // Responsive grid layout
            gridTemplateColumns: {
              xs: "1fr", // Single column on very small screens
              sm: "1.5fr 1fr", // Two columns from small screens up
              md: "2fr 1fr", // Original ratio on medium screens and up
            },
            gap: { xs: 1, sm: 2 }, // Responsive gap
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }} // Stack vertically on very small screens
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ width: "100%", gap: { xs: 2, sm: 3, md: 4 } }}
          >
            <SubjectIcon subject={subject} />
            <Stack direction="column" sx={{ width: "100%", gap: 0.5 }}>
              <Typography
                level="title-lg"
                sx={{
                  fontWeight: 600,
                  color: "neutral.800",
                  textAlign: "left",
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" }, // Responsive font size
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                <span>{title}</span>
              </Typography>
              <Typography
                level="body-md"
                sx={{
                  color: "neutral.600",
                  display: "flex",
                  textAlign: "left",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, // Responsive font size
                  flexWrap: "wrap", // Allow wrapping on small screens
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: examTime ? "success.300" : "warning.300",
                    display: "inline-block",
                  }}
                />
                <span>Exam date:</span>
                <b>{dayjs(examTime).format(DATE_FORMAT) || "To be determined"}</b>
              </Typography>
              <Typography
                level="body-md"
                sx={{
                  color: "neutral.600",
                  display: "flex",
                  textAlign: "left",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, // Responsive font size
                  flexWrap: "wrap", // Allow wrapping on small screens
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: examTime ? "success.300" : "warning.300",
                    display: "inline-block",
                  }}
                />
                <span>Time:</span>
                <b>{dayjs(examTime).format(TIME_FORMAT) || "To be determined"}</b>
              </Typography>
              <Typography
                level="body-md"
                sx={{
                  color: "neutral.600",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, // Responsive font size
                  flexWrap: "wrap", // Allow wrapping on small screens
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: examTime ? "success.300" : "warning.300",
                    display: "inline-block",
                  }}
                />
                Duration: <b>{examDuration || "To be determined"}</b>
              </Typography>
              <Typography
                level="body-md"
                sx={{
                  color: "neutral.600",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, // Responsive font size
                  flexWrap: "wrap", // Allow wrapping on small screens
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: examTime ? "success.300" : "warning.300",
                    display: "inline-block",
                  }}
                />
                Level: <b>{level || "-"}</b>
              </Typography>
              {description && (
                <Typography
                  level="body-md"
                  sx={{
                    color: "neutral.600",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, // Responsive font size
                    flexWrap: "wrap", // Allow wrapping on small screens
                    textTransform: "capitalize"
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: examTime ? "success.300" : "warning.300",
                      display: "inline-block",
                    }}
                  />
                  Paper: <b>{description}</b>
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack
            direction="row"
            sx={{
              justifySelf: { xs: "start", sm: "end" },
              justifyContent: { xs: 'end', md: 'start' },
              gap: { xs: 2, sm: 3, md: 4 },
              mt: { xs: 2, sm: 0 }, // Add margin top on small screens
              width: { xs: "100%", sm: "auto" }, // Full width on small screens
            }}
          >
            <ExamStatus
              type={mappedStatus as TypeExamStatus}
              data={{ examDate: examTime }}
              loading={isLoading}
              // error={hasError}
            />
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
}
